import { TELEFON_ABFRAGE } from "@kundeninfo/ui";
import type { ZiehbarOptionen, ZiehKopfZone } from "@kundeninfo/ui/me-design";
import { KOPF_ZONE_NAME, zielAus } from "./ticketFormat.js";
import type { AblageZiel } from "./ticketFormat.js";

/**
 * UNSERE REGELN FÜR DIE ZIEHHILFEN AUS ME-DESIGN (Spec F126, Fassung 6.1, me-design 4b11d45/84b8db8): Board und
 * Raster melden sich mit derselben Abbildung bei `ziehbar()` an — je Ansicht ein Aufruf. Karten `.tk`,
 * Griff `[data-griff]` (die Karte zeichnet ihn selbst), berechnete Ordnung (F13: keine Einfügelinie,
 * eine Stelle je Zone), nichts verboten, Vorschau und Ziel aus den `data-ablage-*` des Ziels.
 * Board: Zonen = Kopf-Zone „Datum bleibt" (Ziel KOPF, 206) und die Zeilen der Zeitleiste; ohne Zone
 * (zugeklappt, ohne Zeitleiste) KOPF der Spalte (F14). Raster: `spalten: '[data-tag]'` — jede Zelle,
 * jeder Spaltenkopf und jede Spalte ohne Zeitleiste ist ein Ziel (der Helfer trifft in x und y).
 * Ablage, Version, 409 und die Quittung (7 s, Datum aus der Antwort) bleiben beim Board; `abgelegt`
 * liefert dem Helfer unser Rückgängig (Gegenbuchung, F23), am eigenen Platz `'bleibt'` (keine Anfrage,
 * kein Rückgängig-Eintrag im Helfer). Der Helfer ruft `abgelegt` synchron und wartet kein Promise ab:
 * 409/422 kommen erst danach — dann bleibt sein Eintrag mit unserem Rückgängig stehen, wird aber nie
 * gerufen (ohne Quittung kein `detail.rueckgaengig()`, `rueckgaengig` bucht nur gegen die aktuelle
 * Quittung desselben Tickets). `ansage: false`: angesagt wird über unsere Quittung (`me-hinweis`,
 * `role="status"`) bzw. den Hinweis am Board, nicht zweimal.
 */

/** Was die Optionen vom Board brauchen; gelesen über eine Ref, damit `ziehbar` nur beim Einhängen läuft. */
export type ZiehStand = {
  /** Name der Spalte `id` (Ziel KOPF). */
  spaltenName: (id: string) => string;
  /** Legt ab wie „Verschieben …"; `helfer` ist `detail.rueckgaengig` für die Quittung. Synchron:
   *  false = am eigenen Platz (keine Anfrage), true = die Ablage läuft. */
  ablegen: (ticketId: string, ziel: AblageZiel, helfer: () => boolean) => boolean;
  /** Bucht die Ablage `ticketId` gegen (Rückgängig der Quittung, F23). */
  rueckgaengig: (ticketId: string) => void;
};

/** Zonen einer Boardspalte nach der Kopf-Zone: die Zeilen der Zeitleiste. */
export const BOARD_ZONEN = "li.tz__zeile";
/** Ziele im Raster: Zellen, Spaltenköpfe, Spalten ohne Zeitleiste (TicketRaster.tsx). */
export const RASTER_SPALTEN = "[data-tag]";
/** Der Spaltenkopf als Ziel „Datum bleibt" (me-design 4b11d45: Zone `{ kopf: true, name }`). */
export const KOPF_ZONE: ZiehKopfZone = { kopf: true, name: KOPF_ZONE_NAME };

function istKopfZone(z: Element | ZiehKopfZone | null): z is ZiehKopfZone {
  return z !== null && !(z instanceof Element) && z.kopf === true;
}

/** Eine zugeklappte Spalte hat keine Zonen (am Telefon gibt es kein Zuklappen, wie im Helfer). */
function zugeklappt(spalte: Element): boolean {
  const telefon = typeof window.matchMedia === "function" && window.matchMedia(TELEFON_ABFRAGE).matches;
  return spalte.hasAttribute("zugeklappt") && !telefon;
}

/** Das Ablageziel eines Zugs: Kopf-Zone → KOPF; sonst aus der Zone, sonst aus dem Ziel selbst (Raster),
 *  sonst KOPF der Spalte `nach.id` (die Hülle `Boardspalte` reicht keine `data-*` an den Host). */
export function zielVonZug(d: { zone: Element | ZiehKopfZone | null; nach: Element },
                           spaltenName: (id: string) => string): AblageZiel {
  if (istKopfZone(d.zone)) {
    return { columnId: d.nach.id, ziel: "KOPF", datum: null, text: `${spaltenName(d.nach.id)} · ${KOPF_ZONE_NAME}` };
  }
  return zielAus(d.zone) ?? zielAus(d.nach)
    ?? { columnId: d.nach.id, ziel: "KOPF", datum: null, text: spaltenName(d.nach.id) };
}

/** Läuft ein Zug? Am Board meldet me-board `ziehend`; sonst setzt der Helfer `body[data-me-zieht]`
 *  (Zeiger) bzw. `[data-angehoben]` an der Karte (Tastatur). */
export function zugLaeuft(board: Element | null): boolean {
  return (board as unknown as { ziehend?: unknown } | null)?.ziehend === true
    || document.body.hasAttribute("data-me-zieht") || document.querySelector("[data-angehoben]") !== null;
}

/** Die Optionen für `ziehbar()` — `"board"` an `me-board`, `"raster"` am Rollfeld `.tb__raster`. */
export function ziehOptionen(stand: { readonly current: ZiehStand }, ansicht: "board" | "raster"): ZiehbarOptionen {
  const name = (id: string) => stand.current.spaltenName(id);
  const gemeinsam: ZiehbarOptionen = {
    karten: ".tk",
    griff: "[data-griff]",
    ordnung: "berechnet",
    quittung: false,
    ansage: false,
    langdruck: 350,
    name: (karte) => `Ticket ${karte.dataset.nummer ?? ""}`,
    schluessel: (karte) => karte.dataset.ticket ?? "",
    erlaubt: () => true,
    ziel: (_karte, spalte, _index, zone) => zielVonZug({ zone, nach: spalte }, name).text,
    abgelegt: (d) => {
      const ticketId = d.karte.dataset.ticket ?? "";
      if (!stand.current.ablegen(ticketId, zielVonZug(d, name), () => d.rueckgaengig())) return "bleibt";
      return () => stand.current.rueckgaengig(ticketId);
    },
  };
  if (ansicht === "raster") return { ...gemeinsam, spalten: RASTER_SPALTEN };
  return {
    ...gemeinsam,
    zonen: (spalte) => {
      const zeilen = [...spalte.querySelectorAll(BOARD_ZONEN)];
      return zugeklappt(spalte) || zeilen.length === 0 ? [] : [KOPF_ZONE, ...zeilen];
    },
  };
}
